import { useMemo } from 'react';
import { AppDocument } from 'api';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Modal, ModalHeader, Actions, SecondaryButton } from 'components/common/Modal';
import { linkifyHtml } from 'utils/v2/document';

type DocumentDescriptionModalProps = {
    document: AppDocument | null;
    onClose: () => void;
};

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const DescriptionCard = styled.div`
    padding: 18px 20px;
    border-radius: 16px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    background: rgba(248, 250, 252, 0.9);
`;

const DescriptionBody = styled.div`
    font-family: 'Assistant', sans-serif;
    font-size: 17px;
    line-height: 1.65;
    color: #334155;
    word-break: break-word;

    p {
        margin: 0 0 12px;
    }

    p:last-child {
        margin-bottom: 0;
    }

    strong {
        font-weight: 700;
        color: #111827;
    }

    ol,
    ul {
        margin: 0 0 12px;
        padding-left: 22px;
    }

    ol:last-child,
    ul:last-child {
        margin-bottom: 0;
    }

    li {
        margin: 6px 0;
    }

    a {
        color: #6d41d8;
        text-decoration: underline;
        overflow-wrap: anywhere;
    }

    a:hover {
        color: #8353e2;
    }

    h1,
    h2,
    h3 {
        margin: 16px 0 8px;
        color: #111827;
        line-height: 1.3;
    }

    blockquote {
        margin: 0 0 12px;
        padding-left: 12px;
        border-left: 3px solid rgba(131, 83, 226, 0.35);
        color: #475569;
    }
`;

const EmptyText = styled.p`
    margin: 0;
    color: #94a3b8;
    font-size: 14px;
    font-style: italic;
`;

export const getDescriptionHtml = (document: AppDocument, language: string): string | null => {
    const localized =
        language === 'FR'
            ? document.description_fr || document.description_en
            : document.description_en || document.description_fr;
    return localized || document.explanation || null;
};

const DocumentDescriptionModal = ({ document, onClose }: DocumentDescriptionModalProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();

    const rawHtml = document ? getDescriptionHtml(document, language) : null;
    const descriptionHtml = useMemo(() => (rawHtml ? linkifyHtml(rawHtml) : null), [rawHtml]);

    return (
        <Modal
            open={document !== null}
            onClose={onClose}
            size="lg"
            zIndex={1400}
            ariaLabel={t('description', { lng: language })}
        >
            <Content>
                <ModalHeader
                    eyebrow={t('description', { lng: language })}
                    title={document?.document_name ?? ''}
                />
                <DescriptionCard>
                    {descriptionHtml ? (
                        <DescriptionBody dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
                    ) : (
                        <EmptyText>{t('noDescriptionAvailable', { lng: language })}</EmptyText>
                    )}
                </DescriptionCard>
                <Actions>
                    <SecondaryButton type="button" onClick={onClose}>
                        {t('close', { lng: language })}
                    </SecondaryButton>
                </Actions>
            </Content>
        </Modal>
    );
};

export default DocumentDescriptionModal;
